import { inject, Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  doc,
  docData,
  FirestoreInstances,
  orderBy,
  Query,
  query,
  QueryConstraint,
  Timestamp,
  where,
} from '@angular/fire/firestore';
import { EnvironmentService } from '@galaxy/core';
import { Observable } from 'rxjs';
import { ConversationFilters } from './conversation-filters';
import { FIRESTORE_ZERO_TIMESTAMP } from './conversation.constants';
import { ConversationMessage, FirestoreConversation, FirestoreEvent } from './interface/conversation.interface';
import { PlatformLocation } from '@vendasta/conversation';

const validOriginAppLocations = [
  'business_app',
  'partner_center',
  'sales_center',
  'vendor_center',
  'task_manager',
] as const;

type ValidOriginAppLocations = (typeof validOriginAppLocations)[number];

const getFirestore = (name: string) => {
  const fsi = inject(FirestoreInstances);
  return fsi.find((fs) => fs.app.name === name)!;
};

@Injectable()
export class FirestoreService {
  private firestore = getFirestore('inbox');
  private firestoreRootPath = `conversation/env/${this.environmentService.getEnvironmentString()}`;

  constructor(private readonly environmentService: EnvironmentService) {}

  private getFirestoreMessagesRootPath(conversationDocId: string): string {
    return `${this.firestoreRootPath}/${conversationDocId}/messages`;
  }

  private getFirestoreEventsRootPath(conversationDocId: string): string {
    return `${this.firestoreRootPath}/${conversationDocId}/events`;
  }

  conversation$(conversationDocId: string) {
    return docData<FirestoreConversation>(doc(this.firestore, `${this.firestoreRootPath}/${conversationDocId}`));
  }

  setupBaseConversationsQuery(
    organizationId: string,
    filters?: ConversationFilters | null,
    groupId?: string | null,
    skipIsOpenInQuery = false,
    skipIsAnonymousInQuery = false,
  ): Query<FirestoreConversation> {
    let q = query(collection(this.firestore, this.firestoreRootPath), where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP));

    if (groupId) {
      q = query(q, where('subjectParticipantIds', 'array-contains', groupId));
    } else {
      q = query(q, where('subjectParticipantKeyIds', 'array-contains', organizationId));
    }

    // Anonymous should show all anonymous conversations regardless of if they've been closed
    if (!skipIsOpenInQuery && !filters?.isAnonymous) {
      q = query(q, where('isOpen', '==', filters?.isOpen ?? true));
    }

    if (!skipIsAnonymousInQuery) {
      q = query(q, where('isAnonymous', '==', filters?.isAnonymous ?? false));
    }

    return query(
      q,
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  loadOrgRelatedConversationsQuery(organizationId: string): Query<FirestoreConversation> {
    const q = this.setupBaseConversationsQuery(organizationId);
    return query(q, where('channel', '==', 'platform'));
  }

  loadUserRelatedConversationsQuery(userId: string): Query<FirestoreConversation> {
    return query(
      collection(this.firestore, this.firestoreRootPath),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      where('subjectParticipantKeyIds', 'array-contains', userId),
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  // Queries for all conversations between the given user and any AIAssistant
  loadUserRelatedAiAssistantConversationsQuery(
    userId: string,
    location: PlatformLocation,
  ): Query<FirestoreConversation> {
    const constraints: QueryConstraint[] = [
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      // Can't do this because subjectParticipantKey in firestore is an object =/
      // where("subjectParticipantKey", "==", `ai_assistant:${assistantId}|iam_user:${userId}`),
      where('subjectParticipantKeyIds', 'array-contains', userId),
      // Can't do this because firestore only supports one 'array-contains' clause per query
      // where('subjectParticipantKeyIds', 'array-contains', assistantId),
      where('originAppExternalID', '==', ''),
      where('channel', '==', 'AIAssistant'),
    ];

    let originApp: ValidOriginAppLocations | null = null;
    switch (location) {
      case PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
        originApp = 'business_app';
        break;
      case PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
        originApp = 'partner_center';
        break;
    }

    if (originApp) {
      constraints.push(where('originApp', '==', originApp));
    } else {
      constraints.push(where('originApp', 'in', validOriginAppLocations));
    }

    return query(
      collection(this.firestore, this.firestoreRootPath),
      ...constraints,
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  messages$(docId: string) {
    return collectionData(
      query(
        collection(this.firestore, this.getFirestoreMessagesRootPath(docId)),
        where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
        orderBy('created', 'desc'),
      ),
      { idField: 'id' },
    ) as Observable<ConversationMessage[]>;
  }

  webchatMessages$(docId: string) {
    return collectionData(
      query(
        collection(this.firestore, this.getFirestoreMessagesRootPath(docId)),
        where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
        where('channel', '==', 'WebChat'),
        where('type', 'in', ['message', 'booking_availability']),
        orderBy('created', 'desc'),
      ),
      { idField: 'id' },
    ) as Observable<ConversationMessage[]>;
  }

  events$(docId: string) {
    return collectionData(
      query(collection(this.firestore, this.getFirestoreEventsRootPath(docId)), orderBy('happenedAt', 'desc')),
    ) as Observable<FirestoreEvent[]>;
  }

  getViewsConversationsQuery(
    participantId: string,
    viewId: string,
    latestRelevantActivityTime?: Timestamp,
  ): Query<FirestoreConversation> {
    let q = query(
      collection(this.firestore, this.firestoreRootPath),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      where('conversationViewIds', 'array-contains', participantId + ':' + viewId),
      where('originApp', 'in', validOriginAppLocations),
    );
    if (latestRelevantActivityTime) {
      q = query(q, where('latestRelevantActivityTime', '<=', latestRelevantActivityTime));
    }
    return query(
      q,
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  getUnreadConversationsByView(participantId: string, viewId: string): Query<FirestoreConversation> {
    return query(
      collection(this.firestore, this.firestoreRootPath),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP),
      where('unreadConversationViewIds', 'array-contains', participantId + ':' + viewId),
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
      where('originAppExternalID', '==', ''),
    );
  }
}
